/**
 * عمق بازار
 * ==========
 * نمودار تجمعیِ دفتر سفارش: در هر قیمت، **جمع** حجمی که تا آن قیمت برای خرید
 * (سبز، سمت چپ) یا فروش (قرمز، سمت راست) گذاشته شده است.
 *
 * چرا تجمعی و پله‌ای:
 * نمودار عمق به این پرسش پاسخ می‌دهد که «اگر همین حالا فلان مقدار بفروشم،
 * قیمت تا کجا پایین می‌آید؟». پاسخ، جمعِ سفارش‌ها تا آن قیمت است، نه حجم تک‌تک
 * پله‌ها. و چون بین دو پله هیچ سفارشی نیست، خط باید **پله‌ای** باشد نه اریب؛
 * خط اریب یعنی نقدینگی‌ای که وجود ندارد.
 *
 * بدون هیچ کتابخانه تازه‌ای — SVG ساده.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { get } from '../lib/api'
import { clsx, num } from '../lib/format'
import { Book, depthView, probeDepth } from '../lib/depth'

const UP = '#00E39A'
const DOWN = '#FF4D6D'

/** بازه قیمتی که نشان داده می‌شود، به درصد از قیمت میانی */
const ZOOMS = [
  { key: 0.005, label: '٪۰٫۵' }, { key: 0.01, label: '٪۱' },
  { key: 0.02, label: '٪۲' }, { key: 0.05, label: '٪۵' },
  { key: Infinity, label: 'همه' },
]

export function DepthChart({ symbol, quote }: { symbol: string; quote: string }) {
  const box = useRef<HTMLDivElement>(null)
  const [book, setBook] = useState<Book | null>(null)
  const [err, setErr] = useState('')
  const [zoom, setZoom] = useState<number>(0.02)
  const [size, setSize] = useState({ w: 600, h: 300 })
  const [hoverX, setHoverX] = useState<number | null>(null)

  /* ── داده */
  useEffect(() => {
    let alive = true
    const load = () => get<Book>(`/market/orderbook/${symbol}?limit=200`)
      .then((r) => { if (alive) { setBook(r); setErr('') } })
      .catch((e: any) => { if (alive) setErr(e?.message || 'دریافت دفتر سفارش ناموفق بود') })
    load()
    const id = setInterval(load, 4000)
    return () => { alive = false; clearInterval(id) }
  }, [symbol])

  /* ── اندازه ظرف */
  useEffect(() => {
    if (!box.current) return
    const ro = new ResizeObserver(() => {
      const b = box.current?.getBoundingClientRect()
      if (b && b.width > 0) setSize({ w: b.width, h: b.height })
    })
    ro.observe(box.current)
    return () => ro.disconnect()
  }, [])

  const view = useMemo(() => depthView(book, zoom, size), [book, zoom, size])

  const digits = quote === 'IRT' ? 0 : 4
  const cursor = useMemo(() => {
    if (!view || hoverX == null) return null
    return probeDepth(view, hoverX)
  }, [view, hoverX])

  return (
    <div className="flex h-full flex-col">
      {/* ── نوار بالا */}
      <div className="flex flex-wrap items-center gap-2 border-b border-white/[.06] p-2 text-[11px]">
        <span className="text-slate-500">بازه قیمت</span>
        {ZOOMS.map((z) => (
          <button key={z.label} onClick={() => setZoom(z.key)}
            className={clsx('rounded-lg px-2 py-1 transition',
              zoom === z.key ? 'bg-brand-500/20 text-brand-200 ring-1 ring-brand-500/40'
                : 'text-slate-500 hover:bg-white/5 hover:text-slate-300')}>
            {z.label}
          </button>
        ))}
        {view && (
          <span className="ms-auto flex items-center gap-3">
            <span className="text-slate-500">اختلاف خرید و فروش</span>
            <span className="num text-slate-200" dir="ltr">
              {num(view.spread, digits)} ({num(view.spreadPct, 3)}٪)
            </span>
          </span>
        )}
      </div>

      {/* ── نمودار */}
      <div ref={box} className="relative min-h-[220px] flex-1"
        onMouseMove={(e) => {
          const b = box.current?.getBoundingClientRect()
          if (b) setHoverX(e.clientX - b.left)
        }}
        onMouseLeave={() => setHoverX(null)}>
        {view ? (
          <svg width={size.w} height={size.h} className="block">
            {/* شبکه افقی */}
            {view.yTicks.map((t) => (
              <g key={t.y}>
                <line x1={0} x2={size.w} y1={t.y} y2={t.y} stroke="rgba(255,255,255,.05)" />
                <text x={4} y={t.y - 3} fontSize="9" fill="#8B93AD">{num(t.v, 3)}</text>
              </g>
            ))}

            {/* سمت خرید */}
            <path d={view.bidArea} fill={UP} fillOpacity={0.14} />
            <path d={view.bidLine} fill="none" stroke={UP} strokeWidth={1.6} />
            {/* سمت فروش */}
            <path d={view.askArea} fill={DOWN} fillOpacity={0.14} />
            <path d={view.askLine} fill="none" stroke={DOWN} strokeWidth={1.6} />

            {/* قیمت میانی */}
            <line x1={view.midX} x2={view.midX} y1={0} y2={size.h}
              stroke="rgba(255,255,255,.25)" strokeDasharray="4 4" />

            {/* برچسب‌های قیمت */}
            {view.xTicks.map((t) => (
              <text key={t.x} x={t.x} y={size.h - 4} fontSize="9" fill="#8B93AD"
                textAnchor="middle">{num(t.v, digits)}</text>
            ))}

            {/* نشانگر */}
            {cursor && (
              <g>
                <line x1={cursor.x} x2={cursor.x} y1={0} y2={size.h}
                  stroke="rgba(124,92,255,.6)" />
                <circle cx={cursor.x} cy={cursor.y} r={4}
                  fill={cursor.side === 'bid' ? UP : DOWN} />
              </g>
            )}
          </svg>
        ) : (
          <div className="grid h-full place-items-center text-xs text-slate-500">
            {err || (book && !(book.bids?.length && book.asks?.length)
              ? 'در این بازار سفارشی ثبت نشده است'
              : 'در حال دریافت دفتر سفارش…')}
          </div>
        )}

        {/* جعبه خوانش */}
        {cursor && (
          <div className="pointer-events-none absolute top-2 rounded-xl border border-white/10 bg-ink-900/90 px-3 py-2 text-[11px] leading-6 backdrop-blur"
            style={{ left: Math.min(Math.max(8, cursor.x + 12), Math.max(8, size.w - 190)) }}>
            <div className="flex justify-between gap-4">
              <span className="text-slate-500">قیمت</span>
              <span className="num text-slate-100" dir="ltr">{num(cursor.price, digits)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-slate-500">
                {cursor.side === 'bid' ? 'جمع خرید تا این قیمت' : 'جمع فروش تا این قیمت'}
              </span>
              <span className={clsx('num', cursor.side === 'bid' ? 'text-up' : 'text-down')}
                dir="ltr">{num(cursor.amount, 4)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-slate-500">ارزش</span>
              <span className="num text-slate-300" dir="ltr">{num(cursor.value, digits)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-white/[.06] px-3 py-1.5 text-[10px] text-slate-500">
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-sm" style={{ background: UP }} /> خرید
        </span>
        <span>{quote === 'IRT' ? 'قیمت‌ها به تومان' : 'قیمت‌ها به تتر'}</span>
        <span className="flex items-center gap-1">
          فروش <span className="h-2 w-2 rounded-sm" style={{ background: DOWN }} />
        </span>
      </div>
    </div>
  )
}
